/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useCallback, useEffect, useState } from 'react'
import ItemSelector from '@/components/batches/ItemSelector'
import BatchNameForm from '@/components/batches/BatchNameForm'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useFetchItemsWithParamsV2 } from '@/hooks/fetch/useFetchItemsWithParamsV2'
import { useSelectBatchItems } from '@/hooks/items/useSelectBatchItems'
import { useAuth0 } from '@auth0/auth0-react'
import { setErrorCodeAndMessage } from '@/utils/fetch'
import { useForm } from 'react-hook-form'
import DetailedAlert from '@/components/alerts/DetailedAlert'

const ItemBatchCreator = ({
  baseUrl,
  filterOptions,
  getItemsService,
  isFilteredResults,
  itemComponent: ItemComponent,
  itemsKey,
  registerBatchService,
  setItemCustomProps,
  getItemPropByTable,
  sortOptions,
  tableColumns
}) => {
  const [hookSearchParams] = useSearchParams()
  const validateProps = useCallback(() => {
    const requiredProps = [
      { name: 'baseUrl', value: baseUrl },
      { name: 'getItemsService', value: getItemsService },
      { name: 'itemComponent', value: ItemComponent },
      { name: 'registerBatchService', value: registerBatchService },
      { name: 'setItemCustomProps', value: setItemCustomProps }
    ]

    const missingProps = requiredProps
      .filter(prop => !prop.value)
      .map(prop => prop.name)

    return missingProps
  }, [
    baseUrl,
    getItemsService,
    registerBatchService,
    setItemCustomProps,
    ItemComponent
  ])
  const [propValidationErrors, setPropValidationErrors] = useState([])
  const [showError, setShowError] = useState(false)
  const navigate = useNavigate()
  const { getAccessTokenSilently: getAccessToken } = useAuth0()
  const [hasAlert, setHasAlert] = useState(false)
  const [alert, setAlert] = useState()

  const {
    itemIdsKey = 'assetsIds',
    itemsResponse,
    loading,
    onChangePage,
    page,
    searchParams,
    setSearchParams,
    totalPages
  } = useFetchItemsWithParamsV2({
    fetchService: getItemsService
  })
  const {
    isSelectAllMode,
    isSelected,
    toggleSelection: toggleSelectionAsset,
    getIdsArray,
    toggleSelectAllMode
  } = useSelectBatchItems()

  const defaultValues = {}
  const {
    handleSubmit,
    register,
    formState: { errors }
  } = useForm({ defaultValues })
  let currentItemIdsKey = itemIdsKey

  useEffect(() => {
    const missingProps = validateProps()
    setPropValidationErrors(missingProps)
  }, [validateProps])

  const onSubmit = useCallback(
    async ({ name }) => {
      if (propValidationErrors.length > 0) {
        console.error('Cannot submit: Missing required props', propValidationErrors)
        return
      }

      try {
        setHasAlert(false)
        setAlert(null)

        const itemsIds = getIdsArray()
        const hasSelectedItems = itemsIds.length > 0
        const filterValues = {}

        if (isSelectAllMode) {
          filterValues.filter = {
            filterBy: hookSearchParams.get('filterBy'),
            filterText: hookSearchParams.get('filterText')
          }

          currentItemIdsKey = 'excludedIds'
        }

        if (!hasSelectedItems && currentItemIdsKey !== 'excludedIds') {
          setShowError(true)
          console.log(hasSelectedItems)
          return
        }

        setShowError(false)

        const batchResponse = registerBatchService && await registerBatchService(
          { name, [currentItemIdsKey]: itemsIds, ...filterValues },
          getAccessToken
        )

        baseUrl && navigate(`/${baseUrl}/${batchResponse?.id}`)
      } catch (error) {
        setHasAlert(true)
        setAlert(setErrorCodeAndMessage(error))
      }
    },
    [
      baseUrl,
      getAccessToken,
      getIdsArray,
      isSelectAllMode,
      navigate,
      propValidationErrors,
      registerBatchService
    ]
  )

  if (propValidationErrors.length > 0) {
    return <DetailedAlert
      alerts={propValidationErrors}
      description="The following properties are required:"
      status="error"
      title="Component Error"
    />
  }

  const currentItems = itemsResponse
    ? itemsKey
      ? itemsResponse[itemsKey]
      : itemsResponse
    : null

  return (
    <section>
      <BatchNameForm
        alert={alert}
        errors={errors}
        handleSubmit={handleSubmit}
        hasAlert={hasAlert}
        onSubmit={onSubmit}
        register={register}
        showError={showError}
      />
      <ItemSelector
        filterOptions={filterOptions}
        getItemPropByTable={getItemPropByTable}
        isFilteredResults={isFilteredResults}
        isLoading={loading}
        isSelected={isSelected}
        itemComponent={ItemComponent}
        items={currentItems}
        itemsLayout={searchParams.itemsLayout}
        onChangePage={onChangePage}
        onFilterResults={(filter) => setSearchParams(filter)}
        onReset={() => setSearchParams({})}
        page={page}
        searchParams={searchParams}
        setItemCustomProps={setItemCustomProps}
        sortOptions={sortOptions}
        tableColumns={tableColumns}
        toggleSelectAll={toggleSelectAllMode}
        toggleSelection={toggleSelectionAsset}
        totalItems={itemsResponse?.total}
        totalPages={totalPages}
      />
    </section>
  )
}

export default ItemBatchCreator
